// TodaySummaryCard (plan todo 23) — getTodaySummary tool result rendered as
// consumed/remaining/macros with an Open-in-Today navigation action. Values
// come straight from the tool result; null goals stay hidden.

import { Gauge } from "lucide-react";
import { formatNumber } from "../../lib/format";
import type { TodaySummaryData } from "./card-data";
import { ChatCardShell, MacroChip, OpenInButton } from "./card-ui";

export interface TodaySummaryCardProps {
  readonly data: TodaySummaryData;
  readonly onSwitchScreen?: (screen: string) => void;
}

function MacroRow({
  label,
  consumed,
  goal,
}: {
  readonly label: string;
  readonly consumed?: number;
  readonly goal?: number;
}) {
  if (consumed === undefined) return null;
  return (
    <MacroChip
      label={label}
      value={goal !== undefined ? `${formatNumber(consumed)}/${formatNumber(goal)} g` : `${formatNumber(consumed)} g`}
    />
  );
}

export function TodaySummaryCard({ data, onSwitchScreen }: TodaySummaryCardProps) {
  return (
    <ChatCardShell
      icon={Gauge}
      title="Today's summary"
      testId="chat-card-today-summary"
    >
      <div className="rounded-xl bg-accent-light px-3 py-2">
        <div
          className="text-lg font-bold leading-tight text-accent"
          data-testid="today-summary-consumed"
        >
          {formatNumber(data.consumedKcal)}
          {data.goalKcal !== undefined ? ` / ${formatNumber(data.goalKcal)}` : ""} kcal
        </div>
        {data.remainingKcal !== undefined && (
          <div className="text-xs text-accent" data-testid="today-summary-remaining">
            {data.remainingKcal >= 0
              ? `${formatNumber(data.remainingKcal)} kcal remaining`
              : `${formatNumber(Math.abs(data.remainingKcal))} kcal over`}
          </div>
        )}
        {data.burnedKcal !== undefined && data.burnedKcal > 0 && (
          <div className="text-xs text-text-muted">
            {formatNumber(data.burnedKcal)} kcal burned (exercise isn't added back)
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="today-summary-macros">
        <MacroRow label="Protein" consumed={data.consumedProteinG} goal={data.goalProteinG} />
        <MacroRow label="Carbs" consumed={data.consumedCarbsG} goal={data.goalCarbsG} />
        <MacroRow label="Fat" consumed={data.consumedFatG} goal={data.goalFatG} />
        <MacroRow label="Fibre" consumed={data.consumedFiberG} goal={data.goalFiberG} />
      </div>
      <OpenInButton
        label="Open in Today"
        screen="today"
        testId="today-summary-open-btn"
        onSwitchScreen={onSwitchScreen}
      />
    </ChatCardShell>
  );
}
